/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService, CommunicationEntryPoint, EmailDialogConfig, emailDialogRoute, JobDetailsModel, } from '@gh/builder-shared';
import {
  attachmentFromLetter,
  createLinkFromLetter,
  isDeliveryMethodEmail,
} from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import { getRouteInitData } from '@gh/common-shared';
import { ID, PrintEntryPoint } from '@gh/common-shared-data';
import { TranslationService, translationTuple } from '@gh/core-mls';
import {
  cancelAsDefaultAnswer, Confirmation, DialogService, NotificationService, TextMessageDialogConfig, WaitingService, yesAnswer, } from '@gh/core-ui';
import { GridDefinitionFactory, LayoutBasedGridDefinition } from '@gh/core-ui-grid';
import { GetRowIdParams } from 'ag-grid-community';
import { EMPTY$, merge$ } from '@gh/rx';
import { map$, filter$, switchMap$ } from '@gh/rx/operators';
import { assign, noop } from 'lodash';
import { Observable } from 'rxjs';
import { JobDetailsTabBusinessService } from '../../../resources/job-details-tab/job-details-tab-business.service';
import { Letter, LetterListItem } from '../../../resources/job-details-tab/job-details-tab-model';

@Component({
  standalone: false,
  selector: 'gh-job-details-letter-list-tab',
  templateUrl: 'job-details-letter-list-tab.component.html',
})
export class JobDetailsLetterListTabComponent implements OnInit {
  gridDefinition: LayoutBasedGridDefinition;
  letterList: LetterListItem[];
  jobId: ID;

  constructor(private route: ActivatedRoute,
    private gridDefinitionFactory: GridDefinitionFactory,
    private navigationService: BuilderNavigationService,
    private businessService: JobDetailsTabBusinessService,
    private model: JobDetailsModel,
    private waitingService: WaitingService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private translationService: TranslationService) {
  }

  ngOnInit(): void {
    const { gridLayout, letterList, jobId } = getRouteInitData(this.route);

    assign(this, { letterList, jobId });

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased({}, {
      getRowId: (params: GetRowIdParams<LetterListItem>) =>
        params.data != null ? String(params.data.fileName) : 'row-no-data',
    });
    this.gridDefinition.bind(gridLayout);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  onProduce(letterItem: LetterListItem): void {
    this.businessService
      .produceLetter(PrintEntryPoint.Produce, letterItem).pipe(
        this.waitingService.operator(),
        switchMap$((letter) => this.produceLetter(letter)))
      .subscribe({ next: noop, error: this.notificationService.processAll });
  }

  onPreview(letterItem: LetterListItem): void {
    this.businessService
      .produceLetter(PrintEntryPoint.Preview, letterItem).pipe(
        this.waitingService.operator(),
        switchMap$((letter: Letter) => {
          const link = createLinkFromLetter(letter);

          const confirmation$ = this.dialogService.openWithInput(
            textMessageDialogRoute,
            <TextMessageDialogConfig<Confirmation>>{
              title: this.translationService.get('lbl_preview_job_letter'),
              html: `${this.translationService.get('lbl_follow_link_preview_job_letter')}:<br>
                ${link}<br>${this.translationService.get('lbl_click_generate_final_document')}`,
              buttons: [
                { title: this.translationService.get('lbl_confirm'), value: Confirmation.Yes, type: 'primary' },
                { title: this.translationService.get('lbl_cancel'), value: Confirmation.Cancel },
              ],
            }).afterClosed();

          return merge$(
            confirmation$.pipe(filter$(yesAnswer), map$(() => letter)),
            confirmation$.pipe(
              filter$(cancelAsDefaultAnswer),
              switchMap$(() => this.businessService.deleteLetter(letter)),
              switchMap$(() => EMPTY$)),
          );
        }),
        switchMap$((letter) => this.businessService
          .buildFinalVersion(letter, this.model.basicInfo.contactId).pipe(
            this.waitingService.operator())),
        switchMap$((letter) => this.produceLetter(letter)))
      .subscribe({ next: noop, error: this.notificationService.processAll });
  }

  private produceLetter(letter: Letter): Observable<any> {
    if (isDeliveryMethodEmail(letter.deliveryBy)) {
      return this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
        entryPoint: CommunicationEntryPoint.Job,
        jobId: this.jobId,
        contactId: this.model.basicInfo.contactId,
        gFolderId: letter.parents[0],
        email: {
          jobId: this.jobId,
          contactId: this.model.basicInfo.contactId,
          email: this.model.basicInfo.contactEmail,
          attachments: [attachmentFromLetter(letter)],
        },
      }).afterClosed();
    } else {
      const link = createLinkFromLetter(letter);
      return this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
        title: this.translationService.get('lbl_link_to_print_job_letter'),
        html: `${this.translationService.get('lbl_follow_link_print_job_letter')}:\n${link}`,
        buttons: [
          { title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' },
        ],
      }).afterClosed();
    }
  }
}
